import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import {
  HeadingComponentConfig,
  BreadcrumbOption
} from '@rappider/rappider-components/utils';
import { NewProject, Category } from '@rappider/rappider-sdk';
import { PATH_DEFINITIONS, defaultToolbarTitleHeadingSize } from '@rappider/shared/definitions';
import { PROJECT_MODULE_CREATE_FORM_CONFIG } from '@rappider/shared/configs';
import * as ProjectActions from '../../states/projects-state/project.actions';
import { selectIsProjectsLoading, selectPublicProjectCategories } from '../../states/projects-state/project.selectors';

@Component({
  selector: 'rappider-project-module-create',
  templateUrl: './project-module-create.component.html',
  styleUrls: ['./project-module-create.component.scss']
})
export class ProjectModuleCreateComponent implements OnInit, OnDestroy {

  @Output() moduleCreated = new EventEmitter<NewProject>();

  PROJECT_MODULE_CREATE_FORM_CONFIG = PROJECT_MODULE_CREATE_FORM_CONFIG;
  loading = false;
  subscriptions: Subscription[];

  // Title toolbar properties
  mainTitle: HeadingComponentConfig = {
    content: 'PROJECT_MODULE.PROJECT_MODULE_CREATE_COMPONENT.CREATE_NEW_MODULE',
    type: defaultToolbarTitleHeadingSize
  };
  title: string | string[] | BreadcrumbOption[] = [
    {
      label: 'PROJECT_MODULE.PROJECT_LIST_COMPONENT.PROJECT_LIST',
      redirectUrl: PATH_DEFINITIONS.PROJECTS.PROJECT_LIST_PATH
    },
    {
      label: 'PROJECT_MODULE.PROJECT_MODULE_CREATE_COMPONENT.CREATE_NEW_MODULE'
    }
  ];
  displayToolbar = false;
  displayToolbarBackButton = false;

  constructor(
    private store: Store<any>
  ) { }

  ngOnInit(): void {
    this.subscribeToData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToLoadingState(),
      this.subscribeToPublicProjectCategories()
    ];
  }

  /**
 * Subscribe to loading state from store
 */
  private subscribeToLoadingState(): Subscription {
    return this.store.select(selectIsProjectsLoading).subscribe(loading => {
      this.loading = loading;
    });
  }

  /**
   * Subscribe to public project categories and update form options
   */
  private subscribeToPublicProjectCategories(): Subscription {
    return this.store.select(selectPublicProjectCategories).subscribe((categories: Category[]) => {
      if (categories?.length) {
        const categoryInput = this.PROJECT_MODULE_CREATE_FORM_CONFIG.items.find(item => item.fieldName === 'categoryIds');
        if (categoryInput) {
          categoryInput.options = categories.map(category => ({
            key: category.title || category.name,
            value: category.id
          }));
          this.PROJECT_MODULE_CREATE_FORM_CONFIG = { ...this.PROJECT_MODULE_CREATE_FORM_CONFIG };
        }
      }
    });
  }

  /**
   * Handle form submission
   */
  onFormSubmit(formData: any): void {
    if (formData) {
      const moduleProject: NewProject = {
        name: formData.name,
        key: this.generateKeyFromName(formData.name),
        description: formData.description || '',
        categoryIds: formData.categoryIds || [],
        isCreatedByAI: false,
        appType: 'web',
        frontendFramework: 'angular',
        backendFramework: 'loopback4'
      };

      this.createModuleProject(moduleProject);
    }
  }

  generateKeyFromName(name: string): string {
    return name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Create module project using the module project endpoint
   */
  createModuleProject(moduleProject: NewProject): void {
    this.store.dispatch(new ProjectActions.CreateModuleProject({ project: moduleProject }));
  }
}
